import { useEffect, useState } from 'react';
import { Receipt } from 'lucide-react';
import { financialApi, type Reconciliation } from '../api';
import { PageHeader } from '@/lib/ui';

const statusClass: Record<Reconciliation['status'], string> = {
  PENDING: 'bg-amber-500/15 text-amber-300',
  RECONCILED: 'bg-emerald-500/15 text-emerald-300',
  WRITTEN_OFF: 'bg-slate-500/15 text-slate-300',
  DISPUTED: 'bg-rose-500/15 text-rose-300',
};

export function ReconciliationPage() {
  const [rows, setRows] = useState<Reconciliation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      setRows(await financialApi.listPendingReconciliations());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const assign = async (r: Reconciliation) => {
    const engagement = prompt('engagement_id (uuid)') ?? '';
    if (!engagement) return;
    const note = prompt('Note (optional)') ?? '';
    await financialApi.assignReconciliation(r.id, { engagement_id: engagement, note });
    await load();
  };

  const writeOff = async (r: Reconciliation) => {
    const note = prompt('Write-off note (required)') ?? '';
    if (!note.trim()) return;
    await financialApi.writeOffReconciliation(r.id, note);
    await load();
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Expense Reconciliation"
        icon={Receipt}
        subtitle="Match unreconciled fuel + expense entries to engagements, or write them off."
      />

      {error && (
        <div className="mb-4 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded border border-border bg-surface">
        <table className="w-full text-sm">
          <thead className="bg-surface-accent text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-left">Source ID</th>
              <th className="px-3 py-2 text-left">Engagement</th>
              <th className="px-3 py-2 text-left">Asset</th>
              <th className="px-3 py-2 text-left">Period</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => {
              const source = r.fuel_log_id ? 'fuel' : 'expense';
              const sourceId = r.fuel_log_id ?? r.expense_entry_id ?? '';
              return (
                <tr key={r.id} className="hover:bg-surface-accent">
                  <td className="px-3 py-2">{source}</td>
                  <td className="px-3 py-2 font-mono text-xs">{sourceId.slice(0, 8)}…</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.engagement_id?.slice(0, 8) ?? '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.asset_id?.slice(0, 8) ?? '—'}</td>
                  <td className="px-3 py-2">{r.payroll_period ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-1.5 py-0.5 text-xs ${statusClass[r.status]}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="space-x-2 px-3 py-2 text-right">
                    <button className="text-xs text-primary" onClick={() => assign(r)}>
                      assign
                    </button>
                    <button className="text-xs text-rose-300" onClick={() => writeOff(r)}>
                      write off
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !busy && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No pending reconciliations. New expenses appear here for ops review.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
